import { RSelect } from "src/components/common/form/select";
import Nvh from "src/components/common/Nvh";
import { useParentsQuery } from "src/services/react-query-apis/get-parents";
import { useRef, useState } from "react";
import { useMemberQuery } from "src/services/react-query-apis/get-member";
import { Form } from "src/components/common/form";
import { z } from "zod";
import { Button } from "react-bootstrap";
import _ from "lodash";
import { useFormContext } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

const ParentFormWidget = ({
  tondabayashiProps,
  otherSchoolProps,
  readOnly,
}) => {
  const mainLabel = (
    <div className="form-group mb-2 row">
      <label className="col-sm-2 col-form-label text-sm-end"></label>
      <div className="col-sm-4">
        <h4 className="my-1">保護者</h4>
      </div>
      <label className="col-sm-6"></label>
    </div>
  );

  return localStorage.getItem("school_slug") === "tondabayashi" ? (
    <TondabayashiParentFormWidget readOnly={readOnly} {...tondabayashiProps}>
      {mainLabel}
    </TondabayashiParentFormWidget>
  ) : (
    <OtherSchoolParentFormWidget readOnly={readOnly} {...otherSchoolProps}>
      {mainLabel}
    </OtherSchoolParentFormWidget>
  );
};

const OtherSchoolParentFormWidget = ({
  newStudents,
  handleInput,
  readOnly,
  children,
}) => {
  return (
    <>
      {children}
      <div className="form-group mb-2 row">
        <label className="col-sm-2 col-form-label text-sm-end">
          氏名: <span className="text-white">-</span>
        </label>
        <div className="col-sm-4">
          <input
            type="text"
            placeholder="保護者の氏名"
            className="form-control"
            name="guardian_name"
            onChange={handleInput}
            value={Nvh({ value: newStudents.guardian_name })}
            readOnly={readOnly}
          />
        </div>
        <label className="col-sm-2 col-form-label text-sm-end">フリガナ:</label>
        <div className="col-sm-4">
          <input
            type="text"
            placeholder="保護者の氏名：フリガナ"
            className="form-control"
            name="guardian_furigana"
            onChange={handleInput}
            value={Nvh({ value: newStudents.guardian_furigana })}
            readOnly={readOnly}
          />
        </div>
      </div>
    </>
  );
};

const parentsOptions = (parents, selectedParent, selectedParents) => {
  // Convert parents array to option format with value and label
  const availableParents =
    parents?.map(parent => ({
      value: parent.id,
      label: `${parent.id.toString().padStart(4, "0")} ${parent.name}`,
    })) || [];

  // Filter out parents that are already selected
  const unselectedParents = availableParents.filter(
    parent =>
      !selectedParents.some(
        selectedParent => selectedParent.value === parent.value
      )
  );

  // Merge unselected parents with the currently selected parent
  return selectedParent.value !== 0
    ? _.union(unselectedParents, [
        {
          ...selectedParent,
          label: `${selectedParent.value.toString().padStart(4, "0")} ${selectedParent.label}`,
        },
      ])
    : unselectedParents;
};

const TondabayashiParentFormWidget = ({
  parentFormWidgetRef,
  onSubmitted,
  studentId,
  readOnly,
  children,
}) => {
  const memberQuery = useMemberQuery({
    id: studentId,
    queryConfig: {
      enabled: !!studentId,
    },
  });

  const parents = memberQuery.data?.data?.parents || [];

  return (
    <>
      {children}
      <div className="form-group row">
        <label className="col-sm-2 col-form-label text-sm-end"></label>
        <div className="col-sm-10">
          <Form
            key={parents.length}
            id="parent-form-widget"
            formRef={parentFormWidgetRef}
            onSubmit={async values => {
              onSubmitted?.(
                values.parents
                  .map(parent => parent.value)
                  .filter(parent => parent !== 0)
              );
            }}
            schema={z.object({
              parents: z.array(
                z.object({
                  value: z.coerce.number(),
                  label: z.string(),
                })
              ),
            })}
            options={{
              defaultValues: {
                parents: parents.map(parent => ({
                  value: parent.id,
                  label: parent.name,
                })),
              },
            }}
          >
            {({ setValue, watch }) => {
              const formParents = watch("parents");

              return (
                <>
                  {formParents.map((parent, index) => (
                    <div className="d-flex gap-2 mt-3">
                      <ParentFormWidgetSelect
                        key={parent.value}
                        name={`parents.${index}`}
                        readOnly={readOnly}
                      />
                      <Button
                        variant="danger"
                        onClick={() => {
                          setValue(
                            "parents",
                            formParents.filter((_, i) => i !== index)
                          );
                        }}
                        disabled={readOnly}
                      >
                        削除
                      </Button>
                    </div>
                  ))}
                  <div className="col-sm-4 mt-3">
                    <Button
                      variant="primary"
                      onClick={() => {
                        setValue("parents", [
                          ...formParents,
                          { value: 0, label: "保護者を選択" },
                        ]);
                      }}
                      disabled={readOnly || _.last(formParents)?.value === 0}
                    >
                      追加
                    </Button>
                  </div>
                </>
              );
            }}
          </Form>
        </div>
      </div>
    </>
  );
};

const ParentFormWidgetSelect = ({ name, readOnly }) => {
  const [search, setSearch] = useState("");

  const { setValue, watch } = useFormContext();

  const parentsQuery = useParentsQuery({
    paginate: 10,
    page: 1,
    search,
    orderBy: "name",
    orderDirection: "asc",
  });

  return (
    <div className="w-50 m">
      <RSelect
        isLoading={parentsQuery.isPending}
        isDisabled={readOnly}
        options={
          parentsOptions(
            parentsQuery.data?.data?.data,
            watch(name),
            watch("parents")
          ) || []
        }
        onInputChange={setSearch}
        onChange={value => {
          setValue(name, {
            value: value.value,
            label: value.label,
          });
        }}
        placeholder="検索"
        value={watch(name)}
      />
    </div>
  );
};

export const useParentFormWidget = () => {
  const queryClient = useQueryClient();
  const resolvedSubmitRef = useRef(null);
  const parentFormWidgetRef = useRef(null);

  const submitFormAndWait = () => {
    return new Promise(resolve => {
      resolvedSubmitRef.current = resolve;
      parentFormWidgetRef.current.dispatchEvent(
        new Event("submit", { bubbles: true, cancelable: true })
      );
    });
  };

  // this is for the surrounding form
  const preSubmit = async formData => {
    if (localStorage.getItem("school_slug") === "tondabayashi") {
      try {
        const parentIds = await submitFormAndWait();
        parentIds.forEach(parentId => {
          formData.append("member_parents[]", parentId);
        });
      } catch (error) {
        console.log("error", error);
      }
    }
  };

  // this is for the surrounding form
  const postSubmit = () => {
    queryClient.invalidateQueries({
      queryKey: ["member"],
    });
    queryClient.invalidateQueries({
      queryKey: ["parents"],
    });
  };

  return {
    postSubmit,
    preSubmit,
    resolvedSubmitRef,
    parentFormWidgetRef,
  };
};

export default ParentFormWidget;
